import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { sortBy } from 'lodash';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { createHttpParamsFromRecord } from '../secure/common/helpers';
import {
  AccessLogRecord,
  BatchUpdateLocationReviewDemandRequest,
  GetAllLocationsRequest,
  GetLocationCameraOptions,
  GetLocationOptions,
  HasItems,
  LocationCamera,
  LocationDomainWhitelist,
  LocationKeystore,
  LocationResponse,
  LocationReviewDemandResponse,
  LocationReviewNotesResponse,
  ReviewActivityResponse,
  UpdateReviewDemandRequest,
  UpdateReviewNotesRequest,
} from '../types';
import { AssociateLocationObjectsRequest, AType, LocationObjectAssociation } from '../types/location-object';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private httpClient = inject(HttpClient);
  readonly apiHost = `${environment.apiBaseUrl}`;

  // it doesn't get "all" locations, it's scoped to the user permissions
  getAllLocations(request?: GetAllLocationsRequest): Observable<HasItems<LocationResponse>> {
    const requestUrl = `${this.apiHost}/Locations`;
    let params = createHttpParamsFromRecord({
      count_incidents: request?.count_incidents ? 'true' : 'false',
      include_cameras: request?.include_cameras ? 'true' : 'false',
      include_kiosks: request?.include_kiosks ? 'true' : 'false',
      include_profile: request?.include_profile ? 'true' : 'false',
      include_whitelist_domains: request?.include_whitelist_domains ? 'true' : 'false',
      branch_ids: request?.branch_ids?.filter(Boolean).map((id) => id.toString()),
      search: request?.search,
      location_group_ids: request?.location_group_ids?.filter(Boolean),
    });

    if (request?.market_snapshots_enabled) {
      params = params.set('market_snapshots_enabled', 'true');
    }

    if (request?.has_cameras) {
      params = params.set('has_cameras', 'true');
    }

    if (request?.has_plan !== undefined) {
      params = params.append('has_plan', request?.has_plan);
    }

    return this.httpClient
      .get<HasItems<LocationResponse>>(requestUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          ...res,
          items: sortBy(res.items, 'name'),
        })),
      );
  }

  getLocation(request: { locationId: number; options?: GetLocationOptions }): Observable<LocationResponse> {
    const requestUrl = `${this.apiHost}/Locations/${request.locationId}`;
    const options = request.options;
    const params = createHttpParamsFromRecord({
      count_incidents: options?.count_incidents ? 'true' : 'false',
      include_cameras: options?.include_cameras ? 'true' : 'false',
      include_kiosks: options?.include_kiosks ? 'true' : 'false',
      include_profile: options?.include_profile ? 'true' : 'false',
    });
    return this.httpClient.get<LocationResponse>(requestUrl, { params });
  }

  getLocationCameras(request: {
    locationId: number;
    options?: GetLocationCameraOptions;
    bypassCache?: boolean;
  }): Observable<HasItems<LocationCamera>> {
    const { locationId, options, bypassCache } = request;
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Cameras`;
    const params = createHttpParamsFromRecord({
      include_associations: options?.include_associations ? 'true' : 'false',
    });
    return this.httpClient.get<HasItems<LocationCamera>>(requestUrl, {
      params,
      headers: bypassCache ? { 'Cache-Control': 'no-cache' } : undefined,
    });
  }

  getLocationCameraById(request: {
    locationId: number;
    locationCameraId: string;
    options?: GetLocationCameraOptions;
  }): Observable<LocationCamera> {
    const { locationId, locationCameraId, options } = request;
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Cameras/${locationCameraId}`;
    const params = createHttpParamsFromRecord({
      include_associations: options?.include_associations ? 'true' : 'false',
    });
    return this.httpClient.get<LocationCamera>(requestUrl, { params });
  }

  associateCameraWithObject<T extends AType>(request: {
    locationId: number;
    locationCameraId: string;
    payload: AssociateLocationObjectsRequest<T>;
  }): Observable<LocationObjectAssociation<T>> {
    const { locationId, locationCameraId, payload } = request;
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Cameras/${locationCameraId}/Associations/${payload.type}`;
    return this.httpClient.post<LocationObjectAssociation<T>>(requestUrl, payload);
  }

  getLocationSnapshot(url: string) {
    return this.httpClient.get(url, {
      responseType: 'blob',
    });
  }

  deleteCameraAssociation(request: { locationId: number; locationCameraId: string; atype: AType; objectId2: string }): Observable<void> {
    const { locationId, locationCameraId, atype, objectId2 } = request;
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Cameras/${locationCameraId}/Associations/${atype}/${objectId2}`;
    return this.httpClient.delete<void>(requestUrl);
  }

  getLocationAccessLog(branchId: number, locationId: number): Observable<AccessLogRecord[]> {
    const requestUrl = `${this.apiHost}/ReportPortal/Branches/${branchId}/Locations/${locationId}/AccessLog`;
    return this.httpClient.get<AccessLogRecord[]>(requestUrl);
  }

  updateLocationReviewDemands(locationId: number, request: UpdateReviewDemandRequest): Observable<LocationReviewDemandResponse> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/ReviewDemands`;
    return this.httpClient.put<LocationReviewDemandResponse>(requestUrl, request);
  }

  batchUpdateLocationReviewDemands(request: BatchUpdateLocationReviewDemandRequest): Observable<HasItems<LocationReviewDemandResponse>> {
    const requestUrl = `${this.apiHost}/Locations/ReviewDemands`;
    return this.httpClient.patch<HasItems<LocationReviewDemandResponse>>(requestUrl, request);
  }

  getLocationReviewNotes(locationId: number): Observable<LocationReviewNotesResponse> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/ReviewNotes`;
    return this.httpClient.get<LocationReviewNotesResponse>(requestUrl);
  }

  updateLocationReviewNotes(locationId: number, request: UpdateReviewNotesRequest): Observable<LocationReviewNotesResponse> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/ReviewNotes`;
    return this.httpClient.put<LocationReviewNotesResponse>(requestUrl, request);
  }

  getLocationReviewActivity(locationId: number): Observable<ReviewActivityResponse> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/ReviewActivity`;
    return this.httpClient.get<ReviewActivityResponse>(requestUrl);
  }

  // note: if what can be patched changes in the future, we might need to create another type for the request
  patchLocationKeystore(locationId: number, keystore: LocationKeystore) {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/Keystore`;
    return this.httpClient.patch<LocationKeystore>(requestUrl, { keystore });
  }

  addWhitelistDomain(locationId: number, domain: string): Observable<LocationDomainWhitelist> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/AddWhitelistDomain`;
    return this.httpClient.post<LocationDomainWhitelist>(requestUrl, { domain });
  }

  removeWhitelistDomain(locationId: number, domain: string): Observable<LocationDomainWhitelist> {
    const requestUrl = `${this.apiHost}/Locations/${locationId}/RemoveWhitelistDomain/${encodeURIComponent(domain)}`;
    return this.httpClient.delete<LocationDomainWhitelist>(requestUrl);
  }
}
